import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Form, Input, Layout, Space, Typography } from "antd";
import { useAuthenticatedFetch } from "@/Api/Axios";
import { AppLogo } from "@/Assets/Index";
import { useAuth } from "@/Context/AuthContext";
import { UserContext } from "@/Context/UserContext";
import { setLocalStorageItem } from "@/Utils/Index";

const { Text, Title } = Typography;
const { Content, Header } = Layout;

export const Login = ({ onClose }) => {
  const { updateProfileInfo } = useContext(UserContext);
  const { login } = useAuth();
  const fetch = useAuthenticatedFetch();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Get returnUrl from query params for redirect after login
  const searchParams = new URLSearchParams(location.search);
  const returnUrl = searchParams.get("returnUrl");

  // Check for existing authentication on component mount
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    const authToken = localStorage.getItem("authorization");
    if (userId && authToken) {
      const redirectTo = returnUrl ? decodeURIComponent(returnUrl) : "/app/dashboard";
      navigate(redirectTo);
    }
  }, [navigate, returnUrl]);

  const initialValues = {
    email: "",
    password: "",
  };

  const handleSubmit = async (formData) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch.post("/login", formData);

      if (res.data) {
        updateProfileInfo(res.data);
        setLocalStorageItem("authorization", res.data.token);
        setLocalStorageItem("userId", res.data._id);

        await login();

        const redirectTo = returnUrl ? decodeURIComponent(returnUrl) : "/app/dashboard";
        navigate(redirectTo);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Login failed. Please check your credentials and try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const navigateToSignup = () => {
    if (onClose) {
      onClose();
    }
    const signupUrl = returnUrl ? `/app/signup?returnUrl=${returnUrl}` : "/app/signup";
    navigate(signupUrl);
  };

  return (
    <Layout style={{ minHeight: "100vh", background: "#f5f5f5", margin: 0, padding: 0 }}>
      <style>{`
        body { margin: 0 !important; padding: 0 !important; }
        .ant-layout { margin: 0 !important; padding: 0 !important; }
      `}</style>
      <Header
        style={{
          background: "#fff",
          padding: "0 24px",
          height: "64px",
          display: "flex",
          alignItems: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <img src={AppLogo} alt="Upresume.ai" style={{ height: "40px" }} />
      </Header>
      <Content
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "calc(100vh - 64px)",
          padding: "24px",
          margin: 0,
        }}
      >
        <div style={{ width: "100%", maxWidth: "400px" }}>
          <Card style={{ borderRadius: "12px", boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}>
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <div style={{ textAlign: "center" }}>
                <Title level={2} style={{ color: "#1f2937", marginBottom: "8px" }}>
                  Login
                </Title>
              </div>

              {error && (
                <Alert
                  message={error}
                  type="error"
                  showIcon
                  closable
                  onClose={() => setError("")}
                  style={{ marginBottom: "16px" }}
                />
              )}

              <Form
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={initialValues}
                size="large"
                requiredMark={(label, { required }) => (
                  <>
                    {label}
                    {required && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
                  </>
                )}
              >
                <Form.Item
                  label="Email"
                  name="email"
                  validateTrigger="onBlur"
                  rules={[
                    { required: true, message: "Email is required" },
                    { type: "email", message: "Invalid email" },
                  ]}
                >
                  <Input prefix={<UserOutlined />} placeholder="Enter Your Email ID" />
                </Form.Item>

                <Form.Item
                  label="Password"
                  name="password"
                  validateTrigger="onBlur"
                  rules={[
                    { required: true, message: "Password is required" },
                    { min: 6, message: "Password must be at least 6 characters" },
                  ]}
                >
                  <Input.Password prefix={<LockOutlined />} placeholder="Enter Your Password" />
                </Form.Item>

                <div style={{ textAlign: "right", marginBottom: "16px" }}>
                  <Button
                    type="link"
                    onClick={() => navigate("/app/forgot-password")}
                    style={{ padding: 0, fontSize: "14px" }}
                  >
                    Forgot Password?
                  </Button>
                </div>

                <Form.Item style={{ marginBottom: "16px" }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    style={{
                      width: "100%",
                      height: "44px",
                      borderRadius: "8px",
                      borderColor: "#667eea",
                      border: "none",
                    }}
                  >
                    {loading ? "Signing In..." : "Sign In"}
                  </Button>
                </Form.Item>
              </Form>

              <div style={{ textAlign: "center" }}>
                <Text style={{ color: "#6b7280" }}>
                  Don't have an account?{" "}
                  <Button type="link" onClick={navigateToSignup} style={{ padding: 0, fontWeight: "600" }}>
                    Sign up here
                  </Button>
                </Text>
              </div>
            </Space>
          </Card>
        </div>
      </Content>
    </Layout>
  );
};
